import { ElNotification } from 'element-plus';
import type { NotificationHandle } from 'element-plus';
import { h } from 'vue';
import type { VNode } from 'vue';
import AppButton from './AppButton.vue';
import FaIcon from './FaIcon.vue';
import type { IconVariant } from './icon';

export enum NotificationVariant {
  Info = 'info',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Neutral = 'neutral',
}

export interface NotificationAction {
  title: string;
  handler: () => unknown;
  icon?: string;
  iconVariant?: IconVariant;
  /** Closes the notification once `handler` resolves. Defaults to `true`. */
  closeOnAction?: boolean;
}

export const NotificationDefaultTimeout = 6000;

/** Top offset below the status bar, as `AppCore.vue` used to set it. */
const NotificationTopOffset = 16;

/** More than two buttons don't fit next to the message. */
const MaxActions = 2;

// Element Plus has no "neutral" type; its color comes from `.el-notification--neutral`
// in css/components/_element-plus-touch-targets.scss.
const NeutralClass = 'el-notification--neutral';

const notificationTypes: Record<NotificationVariant, 'info' | 'success' | 'warning' | 'error'> = {
  [NotificationVariant.Info]: 'info',
  [NotificationVariant.Success]: 'success',
  [NotificationVariant.Warning]: 'warning',
  [NotificationVariant.Error]: 'error',
  [NotificationVariant.Neutral]: 'info',
};

/**
 * The status bar inset is only available as a CSS variable, but `offset` needs a number.
 * An invisible probe converts it, freshly per call since the inset changes on rotation.
 */
function safeAreaTop(): number {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:var(--safe-top);visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const { height } = probe.getBoundingClientRect();
  probe.remove();

  return height;
}

function renderMessage(message: string, actions: NotificationAction[], close: () => void): VNode {
  return h('div', { class: 'flex flex-col gap-3' }, [
    h('div', message),
    h('div', { class: 'flex flex-wrap gap-2' }, actions.slice(0, MaxActions).map((action) => {
      const { icon, iconVariant } = action;

      return h(
        AppButton,
        {
          key: action.title,
          plain: true,
          handler: async () => {
            await action.handler();
            if (action.closeOnAction !== false) close();
          },
        },
        {
          ...(icon === undefined ? {} : { icon: () => h(FaIcon, { name: icon, variant: iconVariant }) }),
          default: () => action.title,
        },
      );
    })),
  ]);
}

export function useNotification() {
  /**
   * @param timeout Milliseconds until auto-close; `0` stays open.
   */
  function show(
    message: string,
    variant: NotificationVariant = NotificationVariant.Info,
    timeout: number = NotificationDefaultTimeout,
    actions: NotificationAction[] = [],
  ): NotificationHandle {
    // Actions need the handle, which only exists after this call — but they can't be
    // clicked before then either.
    const opened: { handle?: NotificationHandle } = {};
    const close = () => opened.handle?.close();

    opened.handle = ElNotification({
      message: actions.length === 0 ? message : renderMessage(message, actions, close),
      type: notificationTypes[variant],
      customClass: variant === NotificationVariant.Neutral ? NeutralClass : '',
      duration: timeout,
      offset: Math.round(safeAreaTop()) + NotificationTopOffset,
      // Without actions, tapping anywhere on the notification closes it, as before.
      // With actions that would risk a mis-tap.
      onClick: actions.length === 0 ? close : undefined,
    });

    return opened.handle;
  }

  return { show };
}
